import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import MarkPopover from './MarkPopover';
import LessonInfoModal from './LessonInfoModal';

const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DOW_RU = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function pad(n) { return String(n).padStart(2, '0'); }
function monthBounds(yearMonth) {
  const [y, m] = yearMonth.split('-').map(Number);
  const from = `${y}-${pad(m)}-01`;
  const to = new Date(y, m, 0);
  return { from, to: `${y}-${pad(m)}-${pad(to.getDate())}` };
}
function todayMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
function monthAdd(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}
function dayShort(iso) { return Number(iso.slice(8, 10)); }
function fmtDateRu(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
// «Пн 22.02» — подпись дня-источника при переносе выходного.
function fmtTransferSrc(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return `${DOW_RU[d.getDay() || 7]} ${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;
}

// Вкладки тулбара: '' → классный журнал (сетка оценок); 'plan' → тематическое
// планирование (длинный список «дата — тема» за весь учебный год).
const URL_SCHEMA = { subject_id: '', month: '', edit_lesson: '', tab: '' };

/**
 * Электронный дневник для конкретного класса. Используется на двух страницах:
 *   - /journal — стэндалон, родитель сверху рендерит селекторы года и класса
 *   - /classes/:id (вкладка) — встраивается без селектора класса
 *
 * Состояние месяц / предмет / открытый_lesson хранится в URL — это удобно для
 * deep-link и не конфликтует между двумя страницами, т.к. URL разный.
 */
/**
 * @param classId             — id класса (источник предметов и учеников).
 * @param canEdit             — необязательный override для прав редактирования.
 *                              Если не передан, опираемся на админскую матрицу
 *                              `journal.write`. У учителя передаём `canGrade`
 *                              выбранного предмета.
 * @param hideSubjectPicker   — скрыть встроенный список предметов (когда
 *                              выбор предмета поднят в обёртку, например в ЛК
 *                              учителя — там предметы фильтруются по правам).
 */
export default function JournalView({ classId, canEdit, hideSubjectPicker = false }) {
  const can = useCan();
  const canWrite = canEdit !== undefined
    ? !!canEdit
    : can('journal', 'write');
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [classSubjects, setClassSubjects] = useState([]);
  const [gradeComments, setGradeComments] = useState([]);
  const [lateRanges, setLateRanges]       = useState([]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [popover, setPopover] = useState(null);

  // Дефолт месяца — текущий
  useEffect(() => {
    if (!params.month) setParams({ month: todayMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Справочники оценивания: комментарии (для 0–2 классов) и диапазоны опозданий
  useEffect(() => {
    Promise.all([
      api.get('/api/grade-comments').catch(() => ({ rows: [] })),
      api.get('/api/late-ranges').catch(() => ({ rows: [] })),
    ]).then(([c, r]) => {
      setGradeComments(c.rows || []);
      setLateRanges(r.rows || []);
    });
  }, []);

  // Список предметов, реально преподаваемых в классе (по schedule_lessons)
  useEffect(() => {
    if (!classId) { setClassSubjects([]); return; }
    let cancelled = false;
    api.get('/api/journal/subjects?class_id=' + classId)
      .then((res) => {
        if (cancelled) return;
        const rows = res.rows || [];
        setClassSubjects(rows);
        if (params.subject_id && !rows.some((s) => s.id === Number(params.subject_id))) {
          setParams({ subject_id: '' });
        }
      })
      .catch(() => { if (!cancelled) setClassSubjects([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  // Загрузка данных журнала
  const loadJournal = () => {
    if (!classId || !params.subject_id || !params.month) {
      setData(null);
      return;
    }
    const { from, to } = monthBounds(params.month);
    setLoading(true); setError(null);
    api.get(`/api/journal?class_id=${classId}&subject_id=${params.subject_id}&from=${from}&to=${to}`)
      .then((res) => setData(res))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(loadJournal, [classId, params.subject_id, params.month]);

  const marksByCell = useMemo(() => {
    const m = new Map();
    if (!data) return m;
    for (const x of data.marks) {
      if (x.type !== 'daily') continue;
      m.set(x.studentId + ':' + x.date, x);
    }
    return m;
  }, [data]);

  // Триместровые ручные оценки: ключ studentId:dateEnd
  const triMarksByKey = useMemo(() => {
    const m = new Map();
    if (!data) return m;
    for (const x of data.marks) {
      if (x.type !== 'trimester') continue;
      m.set(x.studentId + ':' + x.date, x);
    }
    return m;
  }, [data]);

  // Средние по триместру (key = studentId:trimesterIndex) и за год (key = studentId).
  // Берём предрассчитанные на бекенде агрегаты — на фронте marks только за месяц.
  const avgByStudent = useMemo(() => {
    const out = new Map();
    if (!data?.trimesters?.length) return out;
    for (const t of data.trimesters) {
      const avgs = t.averages || {};
      for (const sid of Object.keys(avgs)) {
        out.set(Number(sid) + ':' + t.index, avgs[sid].avg ?? null);
      }
    }
    return out;
  }, [data]);

  const yearAvgByStudent = useMemo(() => {
    const out = new Map();
    const avgs = data?.year?.averages;
    if (!avgs) return out;
    for (const sid of Object.keys(avgs)) {
      out.set(Number(sid), avgs[sid].avg ?? null);
    }
    return out;
  }, [data]);

  // Годовая ручная оценка: ключ studentId
  const yearMarksByStudent = useMemo(() => {
    const out = new Map();
    if (!data) return out;
    for (const x of data.marks) {
      if (x.type !== 'year') continue;
      out.set(x.studentId, x);
    }
    return out;
  }, [data]);

  // Триместры показываем только в режиме оценок (3–4 классы)
  const isGradesMode = data?.class?.number !== null && data?.class?.number !== undefined && data.class.number >= 3;

  // Плоский список колонок: daily-даты + триместровые avg/mark вставляются
  // сразу после последней daily-даты соответствующего триместра.
  // Если в окно попал конец учебного года — после последнего триместра
  // добавляются годовые «Сред. год» и «Г».
  const columns = useMemo(() => {
    if (!data) return [];
    const out = [];
    const dates = data.dates || [];
    const tris = isGradesMode ? (data.trimesters || []) : [];
    const yr   = isGradesMode ? data.year : null;
    let i = 0;
    for (const t of tris) {
      while (i < dates.length && dates[i].date <= t.dateEnd) {
        out.push({ kind: 'daily', ...dates[i] });
        i++;
      }
      out.push({ kind: 'tri-avg',  trimester: t });
      out.push({ kind: 'tri-mark', trimester: t });
    }
    // Оставшиеся daily-ы — это даты ПОСЛЕ конца последнего триместра
    // (бывает, если последний триместр окончился раньше дат расписания).
    while (i < dates.length) {
      out.push({ kind: 'daily', ...dates[i] });
      i++;
    }
    if (yr) {
      out.push({ kind: 'year-avg',  year: yr });
      out.push({ kind: 'year-mark', year: yr });
    }
    return out;
  }, [data, isGradesMode]);

  const metrics = useMemo(() => {
    if (!data) return null;
    let totalScores = 0, sumScores = 0, fives = 0, twos = 0, late = 0, absent = 0;
    for (const m of data.marks) {
      if (m.type !== 'daily') continue;
      if (!m.presence) absent++;
      if (m.late) late++;
      if (/^[2-5]$/.test(m.score)) {
        const v = parseInt(m.score, 10);
        sumScores += v; totalScores++;
        if (v === 5) fives++;
        if (v === 2) twos++;
      }
    }
    const avg = totalScores ? sumScores / totalScores : null;
    return { avg, fives, twos, late, absent };
  }, [data]);

  const editingLesson = useMemo(() => {
    if (!params.edit_lesson || !data) return null;
    return data.dates.find((d) => d.scheduleLessonId + '_' + d.date === params.edit_lesson) || null;
  }, [params.edit_lesson, data]);

  // Проверяет, был ли ученик зачислен в классе на дату урока. Если у ученика
  // несколько периодов в students_classes — попадание в любой из них считается
  // зачислением.
  const isEnrolledOn = (student, date) => {
    const list = student?.enrollments;
    if (!list || list.length === 0) return false;
    return list.some((e) => (!e.dateStart || e.dateStart <= date) && (!e.dateEnd || date <= e.dateEnd));
  };

  // Клик по ячейке — фокусирует её, чтобы заработала клавиатура (5/4/3/2/Н).
  // Контекстное меню (комментарии, опоздание, удалить) открывается ПКМ —
  // обработчик ниже, в onCellContextMenu.
  const onCellClick = (student, dateInfo, e) => {
    if (!canWrite) return;
    if (!isEnrolledOn(student, dateInfo.date)) return;
    e.currentTarget.focus();
  };

  const onCellContextMenu = (student, dateInfo, e) => {
    e.preventDefault();
    if (!canWrite) return;
    if (!isEnrolledOn(student, dateInfo.date)) return;
    setPopover({
      studentId: student.id,
      date: dateInfo.date,
      scheduleLessonId: dateInfo.scheduleLessonId,
      // Якоримся к точке клика, а не к ячейке — меню сядет рядом с курсором.
      anchorRect: { top: e.clientY, left: e.clientX, bottom: e.clientY, right: e.clientX },
      existing: marksByCell.get(student.id + ':' + dateInfo.date) || null,
    });
  };

  // Прямой ввод 5/4/3/2/Н с клавиатуры. Любая другая клавиша игнорируется,
  // включая стрелки/Tab — стандартное поведение фокуса не ломаем.
  const onCellKeyDown = (student, dateInfo, e) => {
    if (!canWrite) return;
    if (!isEnrolledOn(student, dateInfo.date)) return;
    // Если ввод внутри input/select (например, открыт popover поверх) — пропускаем.
    if (e.target !== e.currentTarget) return;
    const k = e.key;
    let payload = null;
    if (k === '5' || k === '4' || k === '3' || k === '2') {
      payload = { score: k, presence: 1, late: '', comment: '' };
    } else if (k === 'Н' || k === 'н' || k === 'H' || k === 'h') {
      payload = { score: '', presence: 0, late: '', comment: '' };
    } else if (k === 'Delete' || k === 'Backspace') {
      // Удалить отметку: бэк сам сделает DELETE на полностью пустой записи
      payload = { score: '', presence: 1, late: '', comment: '' };
    } else {
      return;
    }
    e.preventDefault();
    api.put('/api/journal/mark', {
      studentId: student.id,
      classId,
      subjectId: Number(params.subject_id),
      scheduleLessonId: dateInfo.scheduleLessonId,
      date: dateInfo.date,
      type: 'daily',
      ...payload,
    }).then((res) => onMarkSaved(res.mark, { fallbackKey: { studentId: student.id, date: dateInfo.date } }))
      .catch((err) => alert(err instanceof ApiError ? err.message : 'Не удалось сохранить'));
  };

  // Триместровая/годовая «итоговая» оценка: только цифры 2..5 + Backspace.
  // Зачисление студента не проверяем — итог может ставиться постфактум.
  const sendItog = (student, e, type, date) => {
    if (!canWrite) return;
    if (e.target !== e.currentTarget) return;
    const k = e.key;
    let payload = null;
    if (k === '5' || k === '4' || k === '3' || k === '2') {
      payload = { score: k, presence: 1, late: '', comment: '' };
    } else if (k === 'Delete' || k === 'Backspace') {
      payload = { score: '', presence: 1, late: '', comment: '' };
    } else {
      return;
    }
    e.preventDefault();
    api.put('/api/journal/mark', {
      studentId: student.id,
      classId,
      subjectId: Number(params.subject_id),
      scheduleLessonId: null,
      date,
      type,
      ...payload,
    }).then((res) => onMarkSaved(res.mark, { fallbackKey: { studentId: student.id, date, type } }))
      .catch((err) => alert(err instanceof ApiError ? err.message : 'Не удалось сохранить'));
  };
  const onTriCellKeyDown  = (student, trimester, e) => sendItog(student, e, 'trimester', trimester.dateEnd);
  const onYearCellKeyDown = (student, year, e)      => sendItog(student, e, 'year', year.dateEnd);

  // mark может быть null (бекенд удалил «пустую» запись). Чтобы знать, какую
  // строку выкинуть из локального state в этом случае, передаём fallbackKey
  // с координатами ячейки.
  const onMarkSaved = (mark, opts = {}) => {
    if (!data) return;
    setData((prev) => {
      const next = { ...prev };
      if (mark === null) {
        const key = opts.fallbackKey || (popover ? { studentId: popover.studentId, date: popover.date } : null);
        if (key) {
          const t = key.type || 'daily';
          next.marks = next.marks.filter((x) =>
            !(x.studentId === key.studentId && x.date === key.date && x.type === t)
          );
        }
      } else {
        const idx = next.marks.findIndex((x) =>
          x.studentId === mark.studentId &&
          x.date === mark.date &&
          x.type === (mark.type || 'daily')
        );
        if (idx >= 0) next.marks = next.marks.map((x, i) => i === idx ? mark : x);
        else next.marks = [...next.marks, mark];
      }
      return next;
    });
    setPopover(null);
    // Если на экране есть триместровые/годовые колонки — пересчитываем агрегаты
    // на бэке. Локальные средние нельзя пересчитать корректно: marks за пределами
    // текущего месяца у нас просто нет.
    const t = mark?.type || opts.fallbackKey?.type || 'daily';
    if (t === 'daily' && (data?.trimesters?.length || data?.year)) {
      loadJournal();
    }
  };

  const onLessonSaved = (lesson) => {
    if (!data) return;
    setData((prev) => {
      const next = { ...prev };
      next.dates = next.dates.map((d) =>
        d.scheduleLessonId === lesson.scheduleLessonId && d.date === lesson.date
          ? { ...d, journalLessonId: lesson.id, theme: lesson.theme, homework: lesson.homework, comment: lesson.comment }
          : d
      );
      return next;
    });
    setParams({ edit_lesson: '' });
  };

  const monthYM = params.month || todayMonth();
  const isPlanTab = params.tab === 'plan';

  return (
    <>
      <div className="jrv-toolbar">
        <div className="jrv-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!isPlanTab}
            className={'jrv-tab' + (!isPlanTab ? ' is-active' : '')}
            onClick={() => setParams({ tab: '' })}
          >
            Классный журнал
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isPlanTab}
            className={'jrv-tab' + (isPlanTab ? ' is-active' : '')}
            onClick={() => setParams({ tab: 'plan' })}
            title="Список «дата — тема» за весь учебный год"
          >
            Тематическое планирование
          </button>
        </div>

        {!isPlanTab && (
          <div className="jrv-period">
            <button className="jr-period-nav" onClick={() => setParams({ month: monthAdd(monthYM, -1) })} title="Предыдущий месяц">
              <Icon name="chevron" size={11} />
            </button>
            <span className="jr-period">
              {MONTHS_RU[Number(monthYM.slice(5, 7)) - 1]} {monthYM.slice(0, 4)}
            </span>
            <button className="jr-period-nav" onClick={() => setParams({ month: monthAdd(monthYM, 1) })} title="Следующий месяц">
              <Icon name="chevron" size={11} />
            </button>
            <button className="jr-period-today" onClick={() => setParams({ month: todayMonth() })}>Сегодня</button>
          </div>
        )}

        {!hideSubjectPicker && (
          <div className="jr-subjects">
            {classSubjects.length === 0 && classId && (
              <div className="jr-subjects__empty">В расписании этого класса нет предметов</div>
            )}
            {classSubjects.map((s) => (
              <button
                key={s.id}
                className={'jr-subj' + (String(s.id) === params.subject_id ? ' is-active' : '')}
                onClick={() => setParams({ subject_id: String(s.id) })}
                style={{ borderLeftColor: s.color || 'var(--border)' }}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {!isPlanTab && metrics && (
          <div className="jrv-metrics">
            <div className="jrv-met"><span className="jrv-met__label">Сред.</span><span className="jrv-met__value">{metrics.avg ? metrics.avg.toFixed(2) : '—'}</span></div>
            <div className="jrv-met"><span className="jrv-met__label">5</span><span className="jrv-met__value jr-met__value--good">{metrics.fives}</span></div>
            <div className="jrv-met"><span className="jrv-met__label">2</span><span className={'jrv-met__value' + (metrics.twos ? ' jr-met__value--bad' : '')}>{metrics.twos}</span></div>
            <div className="jrv-met"><span className="jrv-met__label">Опозд.</span><span className="jrv-met__value">{metrics.late}</span></div>
            <div className="jrv-met"><span className="jrv-met__label">Проп.</span><span className="jrv-met__value">{metrics.absent}</span></div>
          </div>
        )}
      </div>

      {isPlanTab ? (
        <div className="jr-content">
          {!classId ? (
            <div className="ref-empty">Выберите класс.</div>
          ) : !params.subject_id ? (
            <div className="ref-empty">Выберите предмет.</div>
          ) : (
            <ThematicPlanning
              classId={classId}
              subjectId={Number(params.subject_id)}
              canEdit={canWrite}
            />
          )}
        </div>
      ) : (
      <div className="jr-content">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {!classId ? (
          <div className="ref-empty">Выберите класс.</div>
        ) : !params.subject_id ? (
          <div className="ref-empty">Выберите предмет.</div>
        ) : loading || !data ? (
          <div className="ref-empty">Загрузка…</div>
        ) : data.dates.length === 0 ? (
          <div className="ref-empty">В выбранном месяце нет занятий по этому предмету.</div>
        ) : data.students.length === 0 ? (
          <div className="ref-empty">В классе нет учеников за этот период.</div>
        ) : (
          <div className="jr-table-wrap">
            <table className="jr-table">
              <thead>
                <tr>
                  <th className="jr-th-name">Ученик</th>
                  {columns.map((col) => {
                    if (col.kind === 'daily') {
                      const transferHint = col.transferredFrom
                        ? `Перенос выходного: занятия по расписанию ${fmtTransferSrc(col.transferredFrom)}`
                        : null;
                      const themeTitle = [col.theme, col.homework].filter(Boolean).join('\n— ДЗ: ');
                      return (
                        <th
                          key={'d_' + col.scheduleLessonId + '_' + col.date}
                          className={'jr-th-date'
                            + ((col.theme || col.homework) ? ' has-theme' : '')
                            + (col.transferredFrom ? ' is-transfer' : '')}
                          onClick={() => canWrite && setParams({ edit_lesson: col.scheduleLessonId + '_' + col.date })}
                          title={[transferHint, themeTitle].filter(Boolean).join('\n')}
                        >
                          <div className="jr-th-date__num">{dayShort(col.date)}</div>
                          <div className="jr-th-date__pos">{col.position}</div>
                          {(col.theme || col.homework) && <div className="jr-th-date__dot" />}
                        </th>
                      );
                    }
                    if (col.kind === 'tri-avg') {
                      return (
                        <th key={'ta_' + col.trimester.index} className="jr-th-date jr-th-tri jr-th-tri--avg" title={`Средняя за ${col.trimester.index}-й триместр`}>
                          <div className="jr-th-tri__small">сред.</div>
                          <div className="jr-th-tri__big">Т{col.trimester.index}</div>
                        </th>
                      );
                    }
                    if (col.kind === 'tri-mark') {
                      return (
                        <th key={'tm_' + col.trimester.index} className="jr-th-date jr-th-tri jr-th-tri--mark" title={`Итоговая за ${col.trimester.index}-й триместр`}>
                          <div className="jr-th-tri__small">итог</div>
                          <div className="jr-th-tri__big">Т{col.trimester.index}</div>
                        </th>
                      );
                    }
                    if (col.kind === 'year-avg') {
                      return (
                        <th key="ya" className="jr-th-date jr-th-tri jr-th-tri--avg jr-th-year" title="Средняя за год">
                          <div className="jr-th-tri__small">сред.</div>
                          <div className="jr-th-tri__big">Г</div>
                        </th>
                      );
                    }
                    // year-mark
                    return (
                      <th key="ym" className="jr-th-date jr-th-tri jr-th-tri--mark jr-th-year" title="Годовая оценка">
                        <div className="jr-th-tri__small">итог</div>
                        <div className="jr-th-tri__big">Г</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.students.map((st) => (
                  <tr key={st.id}>
                    <td className="jr-td-name">{st.fullName}</td>
                    {columns.map((col) => {
                      if (col.kind === 'daily') {
                        const m = marksByCell.get(st.id + ':' + col.date);
                        const enrolled = isEnrolledOn(st, col.date);
                        const editable = enrolled && canWrite;
                        const cellTitle = !enrolled
                          ? 'Ученик не зачислен в этот период'
                          : (m && m.score === 'К' && m.comment)
                            ? m.comment
                            : (m && m.late ? 'Опоздание: ' + m.late : undefined);
                        return (
                          <td
                            key={'d_' + col.scheduleLessonId + '_' + col.date}
                            className={
                              'jr-td-cell' +
                              (editable ? ' is-editable' : '') +
                              (!enrolled ? ' is-disabled' : '')
                            }
                            tabIndex={editable ? 0 : -1}
                            onClick={(e) => onCellClick(st, col, e)}
                            onContextMenu={(e) => onCellContextMenu(st, col, e)}
                            onKeyDown={(e) => onCellKeyDown(st, col, e)}
                            title={cellTitle}
                          >
                            {m && <Cell mark={m} gradeComments={gradeComments} />}
                          </td>
                        );
                      }
                      if (col.kind === 'tri-avg') {
                        const avg = avgByStudent.get(st.id + ':' + col.trimester.index);
                        return (
                          <td key={'ta_' + col.trimester.index} className="jr-td-cell jr-td-tri jr-td-tri--avg">
                            {avg !== null && avg !== undefined ? (
                              <span className="jr-cell jr-c-other">{avg.toFixed(1)}</span>
                            ) : <span className="jr-cell jr-cell--mute">—</span>}
                          </td>
                        );
                      }
                      if (col.kind === 'tri-mark') {
                        const tm = triMarksByKey.get(st.id + ':' + col.trimester.dateEnd);
                        const editable = canWrite;
                        const s = tm?.score;
                        const cls = s === '5' ? 'jr-c-five' : s === '4' ? 'jr-c-four' : s === '3' ? 'jr-c-three' : s === '2' ? 'jr-c-two' : 'jr-c-other';
                        return (
                          <td
                            key={'tm_' + col.trimester.index}
                            className={'jr-td-cell jr-td-tri jr-td-tri--mark' + (editable ? ' is-editable' : '')}
                            tabIndex={editable ? 0 : -1}
                            onKeyDown={(e) => onTriCellKeyDown(st, col.trimester, e)}
                            onClick={(e) => editable && e.currentTarget.focus()}
                          >
                            {s ? <span className={'jr-cell ' + cls}>{s}</span> : null}
                          </td>
                        );
                      }
                      if (col.kind === 'year-avg') {
                        const avg = yearAvgByStudent.get(st.id);
                        return (
                          <td key="ya" className="jr-td-cell jr-td-tri jr-td-tri--avg jr-td-year">
                            {avg !== null && avg !== undefined ? (
                              <span className="jr-cell jr-c-other">{avg.toFixed(1)}</span>
                            ) : <span className="jr-cell jr-cell--mute">—</span>}
                          </td>
                        );
                      }
                      // year-mark
                      const ym = yearMarksByStudent.get(st.id);
                      const editable = canWrite;
                      const s = ym?.score;
                      const cls = s === '5' ? 'jr-c-five' : s === '4' ? 'jr-c-four' : s === '3' ? 'jr-c-three' : s === '2' ? 'jr-c-two' : 'jr-c-other';
                      return (
                        <td
                          key="ym"
                          className={'jr-td-cell jr-td-tri jr-td-tri--mark jr-td-year' + (editable ? ' is-editable' : '')}
                          tabIndex={editable ? 0 : -1}
                          onKeyDown={(e) => onYearCellKeyDown(st, col.year, e)}
                          onClick={(e) => editable && e.currentTarget.focus()}
                        >
                          {s ? <span className={'jr-cell ' + cls}>{s}</span> : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {popover && (
        <MarkPopover
          anchorRect={popover.anchorRect}
          studentId={popover.studentId}
          classId={Number(classId)}
          classNumber={data?.class?.number ?? null}
          subjectId={Number(params.subject_id)}
          scheduleLessonId={popover.scheduleLessonId}
          date={popover.date}
          existing={popover.existing}
          gradeComments={gradeComments}
          lateRanges={lateRanges}
          onClose={() => setPopover(null)}
          onSaved={onMarkSaved}
        />
      )}

      {editingLesson && (
        <LessonInfoModal
          editing={editingLesson}
          onClose={() => setParams({ edit_lesson: '' })}
          onSaved={onLessonSaved}
        />
      )}
    </>
  );
}

function Cell({ mark, gradeComments }) {
  if (!mark.presence) {
    // Старая БД хранила в score букву (Н/Б/К). Сохраняем символ, стиль — «отсутствие».
    return <span className="jr-cell jr-c-absent">{mark.score || 'Н'}</span>;
  }
  const s = mark.score;
  // Комментарий 0–2 классов: score='К', текст в comment. Цвет — ищем в
  // справочнике grade_comments по совпадению текста (не «вморожен»: если
  // комментарий переименовать в настройках, цвет на ячейке мигрирует).
  if (s === 'К') {
    // Сравниваем с trim — в старых импортированных данных в comment могут
    // быть невидимые пробелы/переводы строк, которые ломают строгое равенство.
    const target = (mark.comment || '').trim();
    const cm = (gradeComments || []).find((c) => (c.text || '').trim() === target);
    const bg = cm?.color || 'var(--panel-2)';
    return (
      <span
        className="jr-cell"
        style={{ background: bg, color: cm ? '#fff' : 'var(--fg)', textShadow: cm ? '0 1px 1px rgba(0,0,0,0.25)' : 'none' }}
      >
        К
        {mark.late && <span className="jr-late-dot" title={'Опоздание ' + mark.late}>⏱</span>}
      </span>
    );
  }
  const cls = s === '5' ? 'jr-c-five' : s === '4' ? 'jr-c-four' : s === '3' ? 'jr-c-three' : s === '2' ? 'jr-c-two' : 'jr-c-other';
  return (
    <span className={'jr-cell ' + cls}>
      {s || '·'}
      {mark.late && <span className="jr-late-dot" title={'Опоздание ' + mark.late}>⏱</span>}
    </span>
  );
}

/**
 * Тематическое планирование — длинный список «дата · урок · тема» за весь
 * учебный год (или произвольный период). Используется учителем как удобная
 * форма заполнения тем уроков пачкой, без переключения месяцев.
 *
 * Поле «Тема» — однострочный textarea, автосейв по blur. Сохраняем только
 * `theme`, домашка и комментарий не трогаем (бекенд использует COALESCE,
 * так что null-поля сохраняют существующее значение).
 *
 * Когда `canEdit=false` (классрук в режиме просмотра) — темы показываются
 * как обычный текст, без инпутов.
 */
function ThematicPlanning({ classId, subjectId, canEdit }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!classId || !subjectId) { setRows([]); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    api.get(`/api/journal/lessons?class_id=${classId}&subject_id=${subjectId}`)
      .then((res) => { if (!cancelled) setRows(res.lessons || []); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [classId, subjectId]);

  // Локально обновляем строку при сохранении (чтобы статус был свежий
  // даже после ре-рендера от других useEffect).
  const updateRow = (scheduleLessonId, date, patch) => {
    setRows((prev) => prev.map((r) =>
      r.scheduleLessonId === scheduleLessonId && r.date === date ? { ...r, ...patch } : r
    ));
  };

  if (loading) return <div className="ref-empty">Загрузка…</div>;
  if (error)   return <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>;
  if (rows.length === 0) {
    return <div className="ref-empty">Нет уроков в учебном году по этому предмету.</div>;
  }

  return (
    <div className="jr-plan">
      <table className="jr-plan__table">
        <thead>
          <tr>
            <th className="jr-plan__th-date">Дата</th>
            <th className="jr-plan__th-pos">Урок</th>
            <th className="jr-plan__th-theme">Тема урока</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <PlanRow
              key={r.scheduleLessonId + '_' + r.date}
              row={r}
              index={idx}
              canEdit={canEdit}
              onSaved={(patch) => updateRow(r.scheduleLessonId, r.date, patch)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Одна строка таблицы тематического планирования. Локальное состояние
 * `value` отслеживает несохранённое значение, `status` показывает
 * визуальный фидбек (saving/saved/error/idle).
 *
 * Сохраняем по blur только если значение реально изменилось (сравниваем
 * с пришедшим с сервера `row.theme`). По Enter — снимаем фокус, что
 * автоматически тригерит blur и сохранение.
 */
function PlanRow({ row, index, canEdit, onSaved }) {
  const [value, setValue]   = useState(row.theme || '');
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [errMsg, setErrMsg] = useState('');
  const savedTimer = useRef(null);

  // Если строка обновилась из родителя (после ре-фетча) — синхронизируем.
  useEffect(() => {
    setValue(row.theme || '');
  }, [row.theme]);

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const save = async () => {
    const current = (row.theme || '');
    if (value === current) return;
    setStatus('saving'); setErrMsg('');
    try {
      const res = await api.put('/api/journal/lesson', {
        scheduleLessonId: row.scheduleLessonId,
        date: row.date,
        theme: value,
      });
      onSaved({
        theme: res.lesson?.theme ?? value,
        journalLessonId: res.lesson?.id ?? row.journalLessonId,
      });
      setStatus('saved');
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setStatus('idle'), 1500);
    } catch (e) {
      setStatus('error');
      setErrMsg(e instanceof ApiError ? e.message : 'Не удалось сохранить');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setValue(row.theme || '');
      setStatus('idle');
      e.currentTarget.blur();
    }
  };

  const d = new Date(row.date + 'T00:00:00');
  const dow = DOW_RU[((d.getDay() || 7))];

  return (
    <tr className={'jr-plan__row jr-plan__row--' + status}>
      <td className="jr-plan__td-date">
        <div className="jr-plan__date-num mono">{fmtDateRu(row.date)}</div>
        <div className="jr-plan__date-dow">{dow}</div>
      </td>
      <td className="jr-plan__td-pos">{row.position}</td>
      <td className="jr-plan__td-theme">
        {canEdit ? (
          <div className="jr-plan__input-wrap">
            <input
              type="text"
              className="jr-plan__input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={save}
              onKeyDown={onKeyDown}
              placeholder="Тема урока"
              tabIndex={index + 1}
            />
            {status === 'saving' && <span className="jr-plan__status jr-plan__status--saving">Сохраняем…</span>}
            {status === 'saved'  && <span className="jr-plan__status jr-plan__status--saved">✓ Сохранено</span>}
            {status === 'error'  && <span className="jr-plan__status jr-plan__status--error" title={errMsg}>✗ Ошибка</span>}
          </div>
        ) : (
          <span className={'jr-plan__theme-ro' + (row.theme ? '' : ' is-empty')}>
            {row.theme || '—'}
          </span>
        )}
      </td>
    </tr>
  );
}
